import React, { useState, useEffect, useCallback } from 'react';
import { useLocale } from '@platform/core';
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../contexts/firebase';
import { usePlanning } from '../contexts/PlanningContext';
import { MagicGrid } from '../components/MagicGrid';

interface Measure {
  Name: string;
  AggType?: string;
}

interface Branch {
  Name: string;
}

interface Department {
  Name: string;
  Branches: Branch[];
}

interface SalesReport {
  id: string;
  Document: { Name: string };
  IntervalType: string;
  SpreadType: string;
  DateRange: { Start: string; End: string };
  Departments: Department[];
  Measures: Measure[];
  width: number;
  _Data?: string;
}

const INTERVAL_TYPES = ['Monthly', 'Quarterly', 'Yearly'];
const SPREAD_TYPES = ['Flat', 'Accumulated'];
const AGG_TYPES = ['SUM', 'AVG', 'COUNT', 'MIN', 'MAX'];

export default function SalesReportPage() {
  const { t } = useLocale();
  const { currentCompany } = usePlanning();
  const [reports, setReports] = useState<SalesReport[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [innerTab, setInnerTab] = useState(0);
  const [hideMeasures, setHideMeasures] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [addDeptMode, setAddDeptMode] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [addBranchDept, setAddBranchDept] = useState<Department | null>(null);
  const [newBranchName, setNewBranchName] = useState('');
  const [addMeasureMode, setAddMeasureMode] = useState(false);
  const [newMeasureName, setNewMeasureName] = useState('');

  const fsGuid = currentCompany
    ? `companies/${currentCompany.id}/SalesReports`
    : null;

  useEffect(() => {
    if (!fsGuid) {
      setReports([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const colRef = collection(db, fsGuid);
    const unsub = onSnapshot(colRef, (snap) => {
      const data = snap.docs.map((d) => ({
        id: d.id,
        Document: { Name: 'Report' },
        IntervalType: 'Monthly',
        SpreadType: 'Flat',
        DateRange: { Start: '', End: '' },
        Departments: [],
        Measures: [],
        width: 3,
        ...d.data(),
      })) as SalesReport[];
      setReports(data);
      setLoading(false);
    });
    return () => unsub();
  }, [fsGuid]);

  const updateReport = useCallback(
    async (id: string, updates: Partial<SalesReport>) => {
      if (!fsGuid) return;
      await setDoc(doc(db, fsGuid, id), updates, { merge: true });
    },
    [fsGuid]
  );

  const createReport = async () => {
    if (!fsGuid) return;
    await addDoc(collection(db, fsGuid), {
      Document: { Name: t('planning.newSalesReport') || 'New Report' },
      IntervalType: 'Monthly',
      SpreadType: 'Flat',
      DateRange: { Start: '', End: '' },
      Departments: [],
      Measures: [],
      width: 3,
      CreatedDate: serverTimestamp(),
    });
  };

  const removeReport = async (id: string) => {
    if (!fsGuid) return;
    await deleteDoc(doc(db, fsGuid, id));
  };

  const toggleMeasure = (name: string) => {
    setHideMeasures((prev) =>
      prev.includes(name) ? prev.filter((m) => m !== name) : [...prev, name]
    );
  };

  const addDepartment = (report: SalesReport) => {
    if (!newDeptName.trim()) return;
    const updated = [
      ...report.Departments,
      { Name: newDeptName, Branches: [] },
    ];
    updateReport(report.id, { Departments: updated });
    setNewDeptName('');
    setAddDeptMode(false);
  };

  const removeDepartment = (report: SalesReport, idx: number) => {
    const updated = report.Departments.filter((_, i) => i !== idx);
    updateReport(report.id, { Departments: updated });
  };

  const addBranch = (report: SalesReport, dept: Department) => {
    if (!newBranchName.trim()) return;
    const updated = report.Departments.map((d) =>
      d === dept
        ? { ...d, Branches: [...d.Branches, { Name: newBranchName }] }
        : d
    );
    updateReport(report.id, { Departments: updated });
    setNewBranchName('');
    setAddBranchDept(null);
  };

  const addMeasure = (report: SalesReport) => {
    if (!newMeasureName.trim()) return;
    const updated = [
      ...report.Measures,
      { Name: newMeasureName, AggType: 'SUM' },
    ];
    updateReport(report.id, { Measures: updated });
    setNewMeasureName('');
    setAddMeasureMode(false);
  };

  const removeMeasure = (report: SalesReport, idx: number) => {
    const updated = report.Measures.filter((_, i) => i !== idx);
    updateReport(report.id, { Measures: updated });
  };

  if (loading) {
    return (
      <div className="sr-loading">
        <div className="loading-spinner" />
        <p>{t('general.loading') || 'Loading...'}</p>
      </div>
    );
  }

  const activeReport = reports[activeTab];

  return (
    <div className="sr-page">
      <div className="page-header">
        <h1>📊 {t('planning.salesReport') || 'Sales Report'}</h1>
        <button className="mg-btn mg-btn-primary" onClick={createReport}>
          ➕ {t('common.add') || 'Add Report'}
        </button>
      </div>

      {/* Report Tabs */}
      {reports.length > 0 && (
        <>
          <div className="sr-tabs">
            {reports.map((r, i) => (
              <button
                key={r.id}
                className={`sr-tab ${i === activeTab ? 'active' : ''}`}
                onClick={() => { setActiveTab(i); setInnerTab(0); }}
              >
                {r.Document?.Name || `Report ${i + 1}`}
              </button>
            ))}
          </div>

          {activeReport && (
            <div className="sr-content">
              {/* Measures toggle */}
              {activeReport.Measures.length > 0 && (
                <div className="sr-measures-bar">
                  <strong>{t('planning.measures') || 'Measures'}:</strong>
                  {activeReport.Measures.map((m) => (
                    <button
                      key={m.Name}
                      className={`mg-btn mg-btn-sm ${
                        hideMeasures.includes(m.Name) ? 'mg-btn-outline' : 'mg-btn-primary'
                      }`}
                      onClick={() => toggleMeasure(m.Name)}
                    >
                      {m.Name}
                    </button>
                  ))}
                </div>
              )}

              {/* Inner tabs */}
              <div className="sr-inner-tabs">
                <button
                  className={`sr-tab ${innerTab === 0 ? 'active' : ''}`}
                  onClick={() => setInnerTab(0)}
                >
                  {t('planning.explore') || 'Explore'}
                </button>
                <button
                  className={`sr-tab ${innerTab === 1 ? 'active' : ''}`}
                  onClick={() => setInnerTab(1)}
                >
                  {t('planning.collect') || 'Collect'}
                </button>
                <button
                  className={`sr-tab ${innerTab === 2 ? 'active' : ''}`}
                  onClick={() => setInnerTab(2)}
                >
                  {t('planning.define') || 'Define'}
                </button>
              </div>

              {/* Explore tab */}
              {innerTab === 0 && activeReport._Data && (
                <div className="sr-explore">
                  <MagicGrid
                    items={JSON.parse(activeReport._Data)}
                    title={activeReport.Document?.Name}
                  />
                </div>
              )}
              {innerTab === 0 && !activeReport._Data && (
                <div className="sr-empty">
                  <p>{t('planning.noData') || 'No data collected yet. Go to Define tab to set up the report structure, then Collect to enter data.'}</p>
                </div>
              )}

              {/* Collect tab — data entry via MagicGrid */}
              {innerTab === 1 && fsGuid && (
                <div className="sr-collect">
                  <MagicGrid
                    firestorePath={`${fsGuid}/${activeReport.id}`}
                    editable
                    addable
                    importable
                    exportable
                  />
                </div>
              )}

              {/* Define tab — report configuration */}
              {innerTab === 2 && (
                <div className="sr-define">
                  <div className="sr-define-grid">
                    {/* Basic settings */}
                    <div className="sr-define-section">
                      <h4>{t('common.name') || 'Name'}</h4>
                      <input
                        className="mg-cell-input"
                        value={activeReport.Document?.Name || ''}
                        onChange={(e) => {
                          const name = e.target.value;
                          updateReport(activeReport.id, {
                            Document: { Name: name },
                          });
                        }}
                      />

                      <h4>{t('planning.intervalType') || 'Interval Type'}</h4>
                      <div className="sr-btn-group">
                        {INTERVAL_TYPES.map((it) => (
                          <button
                            key={it}
                            className={`mg-btn mg-btn-sm ${
                              it === activeReport.IntervalType
                                ? 'mg-btn-primary'
                                : 'mg-btn-outline'
                            }`}
                            onClick={() =>
                              updateReport(activeReport.id, {
                                IntervalType: it,
                              })
                            }
                          >
                            {t(`planning.${it.toLowerCase()}`) || it}
                          </button>
                        ))}
                      </div>

                      <h4>{t('planning.spreadType') || 'Spread Type'}</h4>
                      <div className="sr-btn-group">
                        {SPREAD_TYPES.map((st) => (
                          <button
                            key={st}
                            className={`mg-btn mg-btn-sm ${
                              st === activeReport.SpreadType
                                ? 'mg-btn-primary'
                                : 'mg-btn-outline'
                            }`}
                            onClick={() =>
                              updateReport(activeReport.id, {
                                SpreadType: st,
                              })
                            }
                          >
                            {t(`planning.${st.toLowerCase()}`) || st}
                          </button>
                        ))}
                      </div>

                      <h4>{t('planning.dateRange') || 'Date Range'}</h4>
                      <div className="sr-date-range">
                        <input
                          type="date"
                          className="mg-cell-input"
                          value={activeReport.DateRange?.Start || ''}
                          onChange={(e) =>
                            updateReport(activeReport.id, {
                              DateRange: {
                                ...activeReport.DateRange,
                                Start: e.target.value,
                              },
                            })
                          }
                        />
                        <span>→</span>
                        <input
                          type="date"
                          className="mg-cell-input"
                          value={activeReport.DateRange?.End || ''}
                          onChange={(e) =>
                            updateReport(activeReport.id, {
                              DateRange: {
                                ...activeReport.DateRange,
                                End: e.target.value,
                              },
                            })
                          }
                        />
                      </div>
                    </div>

                    {/* Departments */}
                    <div className="sr-define-section">
                      <h4>{t('planning.departments') || 'Departments'}</h4>
                      {activeReport.Departments.map((dept, di) => (
                        <div key={di} className="sr-dept-card">
                          <div className="sr-dept-header">
                            <strong>{dept.Name}</strong>
                            <button
                              className="mg-btn mg-btn-sm mg-btn-outline"
                              onClick={() => removeDepartment(activeReport, di)}
                            >
                              🗑️
                            </button>
                          </div>
                          <div className="sr-branches">
                            {dept.Branches.map((br, bi) => (
                              <span key={bi} className="sr-branch-tag">
                                {br.Name}
                              </span>
                            ))}
                            {addBranchDept === dept ? (
                              <div className="sr-inline-add">
                                <input
                                  className="mg-cell-input"
                                  placeholder={t('planning.branchName') || 'Branch name'}
                                  value={newBranchName}
                                  onChange={(e) => setNewBranchName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') addBranch(activeReport, dept);
                                    if (e.key === 'Escape') setAddBranchDept(null);
                                  }}
                                  autoFocus
                                />
                                <button className="mg-btn mg-btn-sm mg-btn-primary" onClick={() => addBranch(activeReport, dept)}>
                                  {t('common.add') || 'ADD'}
                                </button>
                                <button className="mg-btn mg-btn-sm mg-btn-outline" onClick={() => setAddBranchDept(null)}>
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <button
                                className="mg-btn mg-btn-sm mg-btn-outline"
                                onClick={() => setAddBranchDept(dept)}
                              >
                                ➕
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      {addDeptMode ? (
                        <div className="sr-inline-add">
                          <input
                            className="mg-cell-input"
                            placeholder={t('planning.departmentName') || 'Department name'}
                            value={newDeptName}
                            onChange={(e) => setNewDeptName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') addDepartment(activeReport);
                              if (e.key === 'Escape') setAddDeptMode(false);
                            }}
                            autoFocus
                          />
                          <button className="mg-btn mg-btn-sm mg-btn-primary" onClick={() => addDepartment(activeReport)}>
                            {t('common.add') || 'ADD'}
                          </button>
                          <button className="mg-btn mg-btn-sm mg-btn-outline" onClick={() => setAddDeptMode(false)}>
                            ✕
                          </button>
                        </div>
                      ) : (
                        <button
                          className="mg-btn mg-btn-sm mg-btn-outline"
                          onClick={() => setAddDeptMode(true)}
                        >
                          ➕ {t('planning.addDepartment') || 'Add Department'}
                        </button>
                      )}
                    </div>

                    {/* Measures */}
                    <div className="sr-define-section">
                      <h4>{t('planning.measures') || 'Measures'}</h4>
                      <div className="sr-measures-list">
                        {activeReport.Measures.map((m, mi) => (
                          <div key={mi} className="sr-measure-tag">
                            <span>{m.Name}</span>
                            <select
                              className="sr-agg-select"
                              value={m.AggType || 'SUM'}
                              onChange={(e) => {
                                const updated = [...activeReport.Measures];
                                updated[mi] = { ...m, AggType: e.target.value };
                                updateReport(activeReport.id, { Measures: updated });
                              }}
                            >
                              {AGG_TYPES.map((a) => (
                                <option key={a} value={a}>{a}</option>
                              ))}
                            </select>
                            <button
                              className="mg-btn mg-btn-sm mg-btn-outline"
                              onClick={() => removeMeasure(activeReport, mi)}
                            >
                              🗑️
                            </button>
                          </div>
                        ))}
                      </div>
                      {addMeasureMode ? (
                        <div className="sr-inline-add">
                          <input
                            className="mg-cell-input"
                            placeholder={t('planning.measureName') || 'Measure name'}
                            value={newMeasureName}
                            onChange={(e) => setNewMeasureName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') addMeasure(activeReport);
                              if (e.key === 'Escape') setAddMeasureMode(false);
                            }}
                            autoFocus
                          />
                          <button className="mg-btn mg-btn-sm mg-btn-primary" onClick={() => addMeasure(activeReport)}>
                            {t('common.add') || 'ADD'}
                          </button>
                          <button className="mg-btn mg-btn-sm mg-btn-outline" onClick={() => setAddMeasureMode(false)}>
                            ✕
                          </button>
                        </div>
                      ) : (
                        <button
                          className="mg-btn mg-btn-sm mg-btn-outline"
                          onClick={() => setAddMeasureMode(true)}
                        >
                          ➕ {t('planning.addMeasure') || 'Add Measure'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="sr-define-actions">
                    <button
                      className="mg-btn mg-btn-primary"
                      onClick={() => updateReport(activeReport.id, { ...activeReport })}
                    >
                      {t('planning.updateSalesReport') || 'Update Sales Report'}
                    </button>
                    <button
                      className="mg-btn mg-btn-outline"
                      style={{ color: 'var(--error-color, #d32f2f)', borderColor: 'var(--error-color, #d32f2f)' }}
                      onClick={() => removeReport(activeReport.id)}
                    >
                      {t('planning.deleteSalesReport') || 'Delete Sales Report'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {reports.length === 0 && (
        <div className="sr-empty-state">
          <p>📊 {t('planning.noSalesReports') || 'No sales reports yet.'}</p>
          <button className="mg-btn mg-btn-primary" onClick={createReport}>
            ➕ {t('planning.createFirst') || 'Create your first report'}
          </button>
        </div>
      )}

      <style>{`
        .sr-page { max-width: 1200px; margin: 0 auto; }
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
        .page-header h1 { margin: 0; font-size: 1.5rem; }

        .sr-tabs, .sr-inner-tabs {
          display: flex; gap: 0; border-bottom: 2px solid var(--border-color, #e0e0e0);
          margin-bottom: 1rem;
        }

        .sr-tab {
          padding: 0.5rem 1rem; border: none; background: transparent;
          cursor: pointer; font-size: 0.875rem; border-bottom: 2px solid transparent;
          margin-bottom: -2px; transition: all 0.15s;
        }

        .sr-tab:hover { background: var(--background-color, #f5f5f5); }
        .sr-tab.active { border-bottom-color: var(--primary-color, #1976d2); color: var(--primary-color, #1976d2); font-weight: 600; }

        .sr-measures-bar {
          display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
          padding: 0.75rem; background: var(--background-color, #f5f5f5);
          border-radius: var(--radius-md, 8px); margin-bottom: 1rem;
        }

        .sr-content { background: var(--surface-color, #fff); border-radius: var(--radius-lg, 12px); border: 1px solid var(--border-color, #e0e0e0); padding: 1rem; }

        .sr-define-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1.5rem; }
        .sr-define-section { padding: 1rem; background: var(--background-color, #f5f5f5); border-radius: var(--radius-md, 8px); }
        .sr-define-section h4 { margin: 0.75rem 0 0.5rem; font-size: 0.875rem; }
        .sr-define-section h4:first-child { margin-top: 0; }

        .sr-btn-group { display: flex; gap: 0.25rem; flex-wrap: wrap; }

        .sr-date-range { display: flex; align-items: center; gap: 0.5rem; }

        .sr-dept-card { border: 1px solid var(--border-color, #e0e0e0); border-radius: var(--radius-md, 8px); padding: 0.75rem; margin-bottom: 0.5rem; background: var(--surface-color, #fff); }
        .sr-dept-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }

        .sr-branches { display: flex; flex-wrap: wrap; gap: 0.375rem; align-items: center; }
        .sr-branch-tag { padding: 0.25rem 0.5rem; background: #e3f2fd; border-radius: 4px; font-size: 0.8125rem; }

        .sr-measures-list { display: flex; flex-direction: column; gap: 0.5rem; }
        .sr-measure-tag { display: flex; align-items: center; gap: 0.5rem; padding: 0.375rem; background: var(--surface-color, #fff); border: 1px solid var(--border-color, #e0e0e0); border-radius: 4px; }
        .sr-agg-select { padding: 0.25rem; border: 1px solid var(--border-color, #e0e0e0); border-radius: 4px; font-size: 0.75rem; }

        .sr-inline-add { display: flex; gap: 0.5rem; align-items: center; margin-top: 0.5rem; }

        .sr-define-actions { display: flex; gap: 0.5rem; margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid var(--border-color, #e0e0e0); }

        .sr-empty, .sr-empty-state { text-align: center; padding: 3rem; color: var(--text-secondary, #757575); }

        .sr-loading { display: flex; flex-direction: column; align-items: center; padding: 3rem; gap: 1rem; }

        .mg-btn { padding: 0.375rem 0.75rem; border-radius: var(--radius-md, 8px); font-size: 0.8125rem; cursor: pointer; border: 1px solid var(--border-color, #e0e0e0); background: var(--surface-color, #fff); transition: all 0.15s; }
        .mg-btn:hover { background: var(--background-color, #f5f5f5); }
        .mg-btn-primary { background: var(--primary-color, #1976d2); color: white; border-color: var(--primary-color, #1976d2); }
        .mg-btn-outline { background: transparent; }
        .mg-btn-sm { padding: 0.25rem 0.5rem; font-size: 0.75rem; }
        .mg-cell-input { padding: 0.375rem 0.5rem; border: 1px solid var(--border-color, #e0e0e0); border-radius: 4px; font-size: 0.875rem; }

        @media (max-width: 768px) {
          .sr-page { padding: 0; }
          .page-header { flex-direction: column; gap: 0.5rem; align-items: stretch; }
          .page-header h1 { font-size: 1.25rem; }

          .sr-tabs, .sr-inner-tabs {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            flex-wrap: nowrap;
          }

          .sr-tab { min-width: max-content; padding: 0.5rem 0.75rem; min-height: 44px; }

          .sr-content { padding: 0.75rem; }

          .sr-define-grid {
            grid-template-columns: 1fr !important;
          }

          .sr-date-range {
            flex-direction: column;
            align-items: stretch;
          }

          .sr-date-range span { text-align: center; }

          .sr-btn-group { flex-wrap: wrap; }

          .sr-define-actions { flex-direction: column; }
          .sr-define-actions .mg-btn { width: 100%; min-height: 44px; }

          .sr-measures-bar { flex-direction: column; align-items: stretch; }

          .sr-inline-add { flex-direction: column; }
          .sr-inline-add .mg-cell-input { min-height: 44px; }

          .mg-btn { min-height: 44px; }
          .mg-cell-input { min-height: 44px; }
        }
      `}</style>
    </div>
  );
}
